/**
 * The private pool, wired to the one gesture allowed to read it.
 *
 * @Codex's delivery on #184, ported onto the API committed in `67c62a6` — the
 * design and the shape of this file are theirs, the names are the ones already
 * on the branch. Decisions 0039 and 0040.
 *
 * The whole point of the class is the ORDER inside `suggest`: snapshot first,
 * then retrieve, aggregate, fold and compile from that snapshot alone. Decision
 * 0039 requires *"one snapshot of eligible genomes"* per invocation, and a
 * function that re-read the store between steps would satisfy every test about
 * its output while breaking the rule the output is supposed to demonstrate.
 */
import {
  aggregate, baselineGenerator, compile, distinctVotes, genomeFromAcceptedLens,
  POOLED_KNOBS, PrivateGenomeStore, retrieveGenomes, tasteFromPoolPrior,
  type GenerationRequest, type Generator, type LensState, type SignalEvent,
  type TastePriors,
} from '@morph/generation';
import type { PoolGenome } from '@morph/spec';

/** The store's shape, so a test can supply a spy and assert that nothing but
 *  the gesture calls `snapshot`. @Codex's delivery on #184 — asserting the
 *  MECHANISM is stronger than inferring it from two compile outputs. */
/**
 * Every string anywhere inside a value, for `assertNoLeak`'s needles.
 *
 * Lives here rather than inside `main.ts`'s `privateValuesNow` because it is
 * the privacy-critical half and it is pure — and because a traversal nothing
 * can call on its own is a traversal nothing can test on its own, which is how
 * it acquired a depth cap nobody measured.
 *
 * ## Cycle-aware, not depth-capped
 *
 * @Codex, P2 on #184: *"When a valid recursive list contains a private string
 * below six container levels, this cutoff silently omits it from
 * `privateValuesNow()`; `KnownValue.list` permits recursive lists and
 * projection preserves those nested arrays. The contribution boundary
 * therefore does not supply the complete projected private values to
 * `assertNoLeak()`, so a projection regression carrying that string would be
 * certified without ever searching for it."*
 *
 * The cap was there for a real reason — the comment it replaces says *"a
 * privacy check is the wrong place to depend on [a value being acyclic]"* —
 * and it answered that reason with a bound that also silently drops legal
 * data. A `WeakSet` answers the same reason exactly: a cycle is visited once
 * and every reachable string is still found.
 *
 * A guard that stops early and reports success is the shape this repository
 * keeps catching: green about a smaller question than the one it names.
 */
export function privateStringsIn(value: unknown, into = new Set<string>()): Set<string> {
  const seen = new WeakSet<object>();
  const walk = (v: unknown): void => {
    if (typeof v === 'string') {
      // No length filter — task `0124` clause 2. `assertNoLeak` filters the
      // needles against the vocabulary itself, so a needle that IS a
      // vocabulary term cannot be evidence and a person with a note called
      // `Go` still contributes.
      if (v.trim().length > 0) into.add(v);
      return;
    }
    if (v === null || typeof v !== 'object') return;
    // The cycle guard, and the whole of what the depth cap was for.
    if (seen.has(v)) return;
    seen.add(v);
    if (Array.isArray(v)) { for (const item of v) walk(item); return; }
    for (const item of Object.values(v as Record<string, unknown>)) walk(item);
  };
  walk(value);
  return into;
}

export interface GenomeSnapshotStore {
  contribute(genome: PoolGenome): string;
  snapshot(): readonly { id: string; genome: PoolGenome }[];
}

/**
 * What the gesture produced — a compile, or a reason there is nothing to show.
 *
 * A discriminated result rather than an outcome that might be a baseline:
 * `compile()` succeeding is not evidence that the POOL said anything, and the
 * caller cannot tell the two apart from a `GenerationOutcome` alone.
 */
export type PoolSuggestion =
  | {
      readonly pooled: true;
      readonly outcome: Awaited<ReturnType<typeof compile>>;
      /** What the pool actually taught. Keep records it as the person's own —
       *  decision 0040: *"accepting the resulting lens creates ordinary personal
       *  evidence through the existing loop."* Without it on the result there was
       *  nothing for the Keep handler to credit, and the loop simply ended. */
      readonly taste: TastePriors;
    }
  | { readonly pooled: false; readonly why: string };

export class PrivatePoolSuggestions {
  readonly #generator: Generator;
  readonly #store: GenomeSnapshotStore;

  /**
   * The genome IDS this pool holds, for a harness that has to ask whether an
   * acceptance contributed.
   *
   * Ids and not genomes, deliberately. A hook that handed back the projections
   * would be a second copy of the contribution boundary with no leak assertion
   * on it — and this exists to check a privacy-shaped claim, so it must not be
   * the hole. What a caller can learn is how many distinct layouts are pooled
   * and whether a gesture added one, which is the whole question.
   */
  ids(): readonly string[] { return this.#store.snapshot().map((row) => row.id); }

  /**
   * The GENERATOR is injected, and the default is not what the demo passes.
   *
   * This file first hard-coded `baselineGenerator`, which was a real defect
   * @Codex's delivery caught: `openOffer` compiles with `stageGenerator` — the
   * one that splits on view state — so a pool offer would have compiled through
   * a different generator than an ordinary offer and lost region splitting, for
   * reasons having nothing to do with the pool. `region-wiring.test.ts` counts
   * `stageGenerator`'s occurrences in `main.ts` precisely so a host that should
   * have it cannot quietly not have it.
   */
  constructor(
    generator: Generator = baselineGenerator,
    store: GenomeSnapshotStore = new PrivateGenomeStore(),
  ) {
    this.#generator = generator;
    this.#store = store;
  }

  /**
   * Keeping a lens contributes; it does not read.
   *
   * `0040`'s last required control is about this asymmetry: *"opening a
   * document, accepting a lens, or contributing without invoking the gesture
   * performs no pool read and cannot repaint an accepted interface."* Nothing
   * here retrieves, aggregates or compiles, and that is the whole of it.
   */
  /**
   * `privateValues` is REQUIRED, and that is the whole of this argument.
   *
   * @Codex, P1 on #184: *"Every real Keep currently calls `contribute()`
   * without a second argument, and this default therefore sends an empty list
   * to `assertNoLeak`, making the product's leak assertion vacuous."*
   *
   * Correct, and it is the worst kind of correct: the tests passed private
   * values and proved the guard works, while the product passed none and got
   * no guard at all. Decision 0039 requires *"the complete projected value
   * through a leak assertion supplied with the private values known at the
   * contribution boundary"* — a default of `[]` satisfies the signature and
   * not the decision.
   */
  contribute(lens: LensState, privateValues: readonly string[]): string | undefined {
    const genome = genomeFromAcceptedLens(lens, privateValues);
    return genome ? this.#store.contribute(genome) : undefined;
  }

  /**
   * The explicit gesture. Snapshot exactly once, then work only from it.
   *
   * `afterSnapshot` exists for the churn control and for nothing else: it lets
   * a test contribute *between* the snapshot and the compile, and prove the
   * result did not move. Without a hook at that instant the clause would have
   * to assert on a race it cannot schedule.
   *
   * ## Why it can refuse, and why that is the important part
   *
   * @Codex, driving the deployed build on #184: in a fresh context with an
   * empty pool, pressing the gesture *"did not refuse … Instead, Morph produced
   * a complete offer whose rationale read 'Suggested from the pool.'"*
   *
   * An empty pool aggregates to nothing, `fold()` then has no base to speak
   * from, and `compile()` cheerfully returns an ordinary baseline layout —
   * which the surface then labelled as a pool suggestion. **That is a layout
   * the pool had no part in, presented to a person as the pool's opinion.** It
   * is the failure this repository hunts hardest: the screen says something
   * happened and the store says nothing did.
   *
   * So the refusal is on the TASTE the pool actually produced, not on the
   * snapshot being empty. A pool that holds genomes and abstains — every knob
   * tied, decision 0038 — has equally said nothing, and compiling that would
   * tell the same lie by a longer route.
   */
  async suggest(
    request: GenerationRequest,
    ledger: readonly SignalEvent[] = [],
    afterSnapshot?: () => void,
  ): Promise<PoolSuggestion> {
    const snapshot = this.#store.snapshot();
    afterSnapshot?.();
    const retrieved = retrieveGenomes(snapshot, request);
    const prior = aggregate(retrieved);
    const now = Date.now();
    /**
     * What the POOL alone says, with no ledger behind it.
     *
     * @Codex, P2 on #184: *"When `suggest()` is called with an empty snapshot
     * and a ledger containing a global density, typography, or palette
     * observation, `tasteFromPoolPrior()` returns the personal winner even
     * though `aggregate()` contributed nothing. The nonempty `pooled` object
     * then makes this path return `pooled: true` and present a purely
     * personal/baseline result as 'Suggested from the pool'."*
     *
     * The same lie the empty-pool refusal was written for, by a second route,
     * and the refusal could not see it: it asked whether any taste came out of
     * `fold()`, and a person's own ledger answers that. Asking the pool without
     * the ledger is the question that was meant.
     */
    /**
     * At the REQUEST's scope, both times.
     *
     * `0040` makes `fold()` the one place a pool base and personal evidence
     * meet, and a person's context-scoped preference has to beat a pool-only
     * global bucket there — which it cannot if the reader asks for `@global`.
     * @Codex, P2 on #184. The context is the only part of the scope this
     * boundary knows; a surface scope would need the lens identity, and
     * `resolve` inherits upward from whatever it is given.
     */
    const scope = { level: 'context' as const, context: request.context };
    const poolAlone = tasteFromPoolPrior(prior, now, [], scope, distinctVotes(retrieved));
    if (Object.keys(poolAlone).length === 0) {
      return { pooled: false, why: 'nothing in the pool fits this yet' };
    }
    const pooled = tasteFromPoolPrior(prior, now, ledger, scope, distinctVotes(retrieved));
    // The person's own taste wins where they have one. The pool is a base, not
    // an override — decision 0040 keeps `fold()` as the single place the two
    // meet, and this spread is the demo saying the same thing.
    const taste = { ...pooled, ...request.taste };
    /**
     * Only the candidates that actually voted.
     *
     * @Codex, P2 on #184: *"`aggregate()` correctly gives the latter no weight,
     * but this call still passes the complete retrieved array to `compile()`.
     * The compiler records every supplied `genomeId` in `provenance.priors`,
     * whose contract says these are genomes that informed the result, so
     * attribution and pool-helpfulness measurements credit candidates that had
     * no effect."*
     *
     * A zero score contributes nothing to any knob's total, so naming it in
     * provenance is a claim about influence that the arithmetic contradicts.
     */
    /**
     * Positive scores only, and each id ONCE.
     *
     * @Codex, P2 on #184: *"When an injected snapshot store returns the same
     * positive-scored `genomeId` more than once, `aggregate()` correctly
     * deduplicates its vote but this list still contains every copy, so
     * `compile()` records duplicate IDs in `provenance.priors`. The resulting
     * `GenerationOutcome` therefore changes despite decision 0040 requiring
     * duplication of a genome ID to have no effect."*
     *
     * Two places deduplicating and one of them forgetting is exactly the shape
     * `0040`'s rule exists to prevent — *"ten copies of one genome cannot become
     * ten times as persuasive"* — and provenance is where persuasiveness is
     * read from afterwards. The vote was safe and the RECORD of the vote was
     * not, which is a difference nothing downstream can see.
     */
    /**
     * The intersection FIRST, and a refusal when it is empty.
     *
     * @Codex, P2 on #184: *"When the pool has an opinion but the person's
     * request or ledger overrides all three pooled knobs, `poolTaught(poolAlone,
     * taste)` is empty, yet this still returns `pooled: true` and compiles a
     * layout determined solely by personal taste. The UI then labels that result
     * 'Suggested from the pool' and Keep records that a pool layout was accepted
     * even though no pool value survived."*
     *
     * The third route to the same lie, and the empty-pool refusal above was
     * written for the first two: *"a baseline layout labelled 'Suggested from
     * the pool' is a layout the pool had no part in."* That refusal asks whether
     * the pool has an opinion. This one asks whether any of it SURVIVED — and a
     * pool that is outvoted on every knob it may vote on contributed exactly as
     * much as an empty one.
     */
    /**
     * What the person's own evidence says with NO pool behind it.
     *
     * The fourth route to the same lie, and each of the first three closed one
     * door while leaving this one open. @Codex, P2 on #184:
     *
     * > *"When the request or ledger already prefers `dense` and the pool also
     * > votes `dense`, this equality treats the value as pool-taught even
     * > though withholding the pool produces the same effective taste and
     * > compiled layout. The UI therefore returns `pooled: true`, labels the
     * > unchanged result 'Suggested from the pool,' and credits Keep to the
     * > pool despite decision 0039 requiring a compiled-layout difference
     * > caused by the prior."*
     *
     * An empty prior through the same `fold()`, at the same scope, so what
     * comes back differs from `taste` in exactly one respect: the pool is not
     * in it. That is the counterfactual the claim needs — *"would this layout
     * have been different"* — and it costs one fold over a ledger the caller
     * already holds.
     */
    /**
     * The ACCEPTED spec's values are in the counterfactual too — the fifth
     * route to the same lie.
     *
     * @Codex, P2 on `dcc6189`: *"When the accepted spec already has
     * `density: 'dense'`, neither the request nor ledger expresses a density
     * preference, and the pool also votes `dense`, this no-pool counterfactual
     * contains no density value, so `poolTaught()` incorrectly treats `dense`
     * as caused by the pool. Compiling without the pool would actually
     * preserve the accepted spec's existing density and produce the same
     * `LayoutSpec`."*
     *
     * The previous four rounds all asked *"would the TASTE have been
     * different"*, and `0039` asks whether the compiled LAYOUT would have
     * been. Those were the same question until `0045` made generation a
     * revision — after which a knob nobody instructs is not absent from the
     * result, it is whatever the person already had. So a counterfactual built
     * from taste alone reports the pool as the cause of every value the
     * arrangement already carried.
     *
     * Read per KNOB rather than spreading the spec: `POOLED_KNOBS` is what the
     * pool can vote on, and a spec carries fields — `target`, `root`,
     * `viewState` — that are not taste and would corrupt the comparison.
     *
     * Under `request.taste`, not over it: an explicit instruction outranks the
     * arrangement, which is `reviseAccepted`'s own precedence. And under the
     * ledger's fold as well, for the same reason — a person's own recorded
     * gestures are instructions, and `fold` has already weighed them.
     */
    const accepted = request.accepted as Record<string, unknown> | undefined;
    const preserved: Record<string, unknown> = {};
    if (accepted) {
      for (const knob of POOLED_KNOBS) {
        const value = accepted[knob];
        if (typeof value === 'string') preserved[knob] = value;
      }
    }
    const withoutPool = {
      ...preserved,
      ...tasteFromPoolPrior({}, now, ledger, scope, 0),
      ...request.taste,
    };
    const taught = poolTaught(poolAlone, taste, withoutPool);
    if (Object.keys(taught).length === 0) {
      return { pooled: false, why: 'the pool agrees with nothing you have not already chosen' };
    }
    /**
     * THE COMPILED COUNTERFACTUAL, not the taste-only one — @Codex, P2 on
     * `18eae3f`, the fifth round on this seam and the one that ends it. When
     * the generator's own deterministic inference would pick the same value
     * the pool votes — the shape naturally produces `dense`, and the pool
     * agrees — no taste map can say so: the value is in no request, no
     * ledger, no accepted spec, and only the compiler knows it. So the
     * question 0039 actually asks ("would the LAYOUT have been different?")
     * is answered by compiling without the pool and comparing what came out.
     * Four taste-side approximations each moved the mislabel one case over;
     * the compile is the fact itself. One extra compile per pool-suggest —
     * a person-level gesture, not a measured path.
     */
    const alone = await compile({ ...request, taste: withoutPool }, this.#generator);
    /**
     * Deduplicated FIRST, then scored — in that order, and the order is the
     * whole of this round's fix.
     *
     * @Codex, P2 on #184: *"`aggregate()` keeps the first row for a
     * `genomeId`, but the positive-score test here runs before the dedupe, so
     * a genome whose first row scores `0` and whose second row scores
     * positively is skipped at the first row and admitted at the second. The
     * genome contributed no weight and is still named in
     * `provenance.priors`."*
     *
     * Right, and it is the previous two findings' own fix leaving its mirror
     * unhandled: both were about naming a genome that did not inform, and
     * chaining their two tests in the convenient order reopened exactly that.
     * `aggregate()` decides on the FIRST row it sees for an id, so the first
     * row is the only one whose score is evidence about influence — a later
     * copy is a duplicate whose weight was already discarded.
     */
    const first = new Set<string>();
    const informing = retrieved.filter((candidate) => {
      if (first.has(candidate.genomeId)) return false;
      first.add(candidate.genomeId);
      return candidate.score > 0;
    });
    const outcome = await compile({ ...request, taste, poolPriors: informing }, this.#generator);
    // Identical layouts mean the pool changed nothing, whatever the taste
    // maps say — the UI must not claim "Suggested from the pool" and Keep
    // must not record pool-taught evidence for a result the person would
    // have been offered anyway. Compared only when both compiles succeeded:
    // a pool whose priors turn a refusal into a layout genuinely caused it.
    if (outcome.ok && alone.ok
      && JSON.stringify(outcome.spec) === JSON.stringify(alone.spec)) {
      return { pooled: false, why: 'the pool agrees with what the layout would already be' };
    }
    return {
      pooled: true,
      taste: taught,
      outcome,
    };
  }
}

/**
 * What the pool taught AND the person accepted — the intersection, not either
 * side of it.
 *
 * Two findings meet on this line and pull in opposite directions, which is why
 * it is a function with a name rather than an expression.
 *
 * It returned the FOLD's conclusion first. @Codex: keeping records the reported
 * taste as the person's own, so returning the fold would credit them with their
 * own prior evidence and call it the pool's.
 *
 * It then returned `poolAlone`, and that was wrong the other way. @Codex, P2 on
 * #184: *"line 158 correctly lets the person's taste override the pool for
 * compilation, but this returns `poolAlone` … a pool voting `dense` can produce
 * a lens compiled with the person's `comfortable` preference, yet keeping that
 * lens records `dense`, the opposite of what they accepted."*
 *
 * Both are satisfied by the intersection. A knob the person overrode was not
 * taught by the pool and is not in the lens they kept, so recording it is wrong
 * twice over; a knob where the pool's value survived into the compiled taste is
 * exactly what the pool contributed to the thing they pressed Keep on.
 *
 * And a THIRD condition, which the intersection alone does not give. @Codex,
 * P2 on the round after: agreement is not teaching. If the person's own
 * evidence already said `dense` and the pool also says `dense`, the value
 * survives the intersection while the layout is identical to the one they
 * would have got with no pool at all. `withoutPool` is that counterfactual,
 * and it is the difference between "the pool agreed with you" and "the pool
 * changed what you saw".
 */
function poolTaught(
  fromPool: TastePriors, effective: TastePriors, withoutPool: TastePriors,
): TastePriors {
  const kept: Record<string, unknown> = {};
  for (const [knob, value] of Object.entries(fromPool)) {
    // Survived into the compiled taste...
    if ((effective as Record<string, unknown>)[knob] !== value) continue;
    // ...AND is not what the person would have got anyway. Agreement is not
    // teaching: a pool that votes for the value somebody had already chosen
    // has changed no layout, and decision 0039 asks for a difference the prior
    // caused rather than for a value the prior happens to share.
    if ((withoutPool as Record<string, unknown>)[knob] === value) continue;
    kept[knob] = value;
  }
  return kept as TastePriors;
}
